import type {
  MeView,
  PlatformScores,
  ScoreCell,
  ScorePosition,
  ScoreSpread,
  TenantScore,
} from '../api.ts';

/**
 * The words around a staff-only score (ADR-0081), and the one decision about
 * who may see one.
 *
 * Kept out of the components for two reasons. The wording is the whole of what
 * keeps a score defensible — "78.4" alone is the number ADR-0007 refused, and
 * "78.4 · 31 of 40 assessed, 6 not evidenced" is the one it can live with — so
 * it is written once and tested. And the gate is a function rather than an
 * `&&` repeated in three files, because the failure it prevents is a customer
 * seeing a rank, and that should have one place to be wrong in.
 */

/** Printed beside every score, on every screen that shows one. */
export const STAFF_ONLY_LABEL = 'Staff only — not shown to clients';

/**
 * Whether this person's console may ask for scores at all. Asking is itself the
 * disclosure: a request a customer's browser makes, even one the server
 * refuses, names the feature in their network tab.
 */
export const mayLoadScores = (me: MeView): boolean => me.platformOwner;

/**
 * The score for one client row, or null for anybody who is not staff.
 *
 * Null for a customer whatever `scores` holds, so that a payload which somehow
 * reached a customer's console still renders nothing.
 */
export const staffScoreFor = (
  me: MeView,
  scores: PlatformScores | null,
  tenantId: string,
): TenantScore | null => {
  if (!mayLoadScores(me) || scores === null) return null;
  return scores.tenants.find((entry) => entry.tenantId === tenantId) ?? null;
};

export const assessedCount = (cell: ScoreCell): number => cell.passed + cell.failed;

/** Everything in scope for this cell, whatever its verdict. */
export const inScopeCount = (cell: ScoreCell): number =>
  cell.passed + cell.failed + cell.notApplicable + cell.notEvidenced;

/** A score to one decimal, or a sentence where there is none — never a zero. */
export const scoreText = (score: number | null): string =>
  score === null ? 'Not scored' : score.toFixed(1);

/**
 * What a score rests on. Not evidenced is always named when it is not zero:
 * those controls are outside the denominator, and a reader who cannot see how
 * many there are cannot tell a well-read tenant from a barely-read one.
 * `short` drops not applicable, which describes the tenant rather than our
 * view of it, for the cramped per-framework columns.
 */
export const coverageText = (cell: ScoreCell, short = false): string => {
  const total = inScopeCount(cell);
  if (total === 0) return 'No controls in scope';
  const parts = [`${assessedCount(cell)} of ${total} assessed`];
  if (cell.notEvidenced > 0) parts.push(`${cell.notEvidenced} not evidenced`);
  if (!short && cell.notApplicable > 0) parts.push(`${cell.notApplicable} not applicable`);
  return parts.join(', ');
};

/** The score and its coverage as one line, for a screen reader or a title. */
export const scoreWithCoverage = (cell: ScoreCell): string =>
  cell.score === null
    ? `Not scored · ${coverageText(cell)}`
    : `${scoreText(cell.score)} · ${coverageText(cell)}`;

/**
 * A tenant with no completed assessment has no score and no coverage — saying
 * "Not scored · No controls in scope" about it would describe the catalogue,
 * when the truth is that we have not looked yet.
 */
export const isAssessed = (tenant: Pick<TenantScore, 'completedAt'>): boolean =>
  tenant.completedAt !== null;

export const NOT_YET_ASSESSED = 'Not yet assessed';

export const rankText = (position: ScorePosition | null): string =>
  position === null ? 'Unranked' : `${position.rank} of ${position.of}`;

/** English ordinals, including the teens that break the last-digit rule. */
export const ordinal = (value: number): string => {
  const teen = value % 100;
  if (teen >= 11 && teen <= 13) return `${value}th`;
  switch (value % 10) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
};

export const percentileText = (position: ScorePosition | null): string =>
  position === null ? '—' : `${ordinal(Math.round(position.percentile))} percentile`;

/** One end of a spread, or a dash where nobody was scored. */
export const spreadValue = (value: number | null): string =>
  value === null ? '—' : value.toFixed(1);

export const spreadRangeText = (spread: ScoreSpread): string =>
  spread.min === null || spread.max === null
    ? 'Nobody scored'
    : `${spreadValue(spread.min)}–${spreadValue(spread.max)}, median ${spreadValue(spread.median)}`;

/**
 * Highest score first, unscored last whichever way the list is turned: an
 * unscored tenant is not the worst tenant, and sorting it below a 12.0 would
 * say it was.
 */
export const compareScores = (
  a: number | null,
  b: number | null,
  direction: 'descending' | 'ascending',
): number => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return direction === 'descending' ? b - a : a - b;
};
